import React, { useState, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  useTheme,
  Button,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  Campaign as CampaignIcon,
  AssignmentTurnedIn as RFQIcon,
  Analytics as AnalyticsIcon,
  PeopleAlt as PartnersIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Storefront as StorefrontIcon,
  ExpandMore,
  Factory as FactoryIcon,
  LocalShipping as SupplierIcon,
  Straighten as MeasurementIcon,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import KamChatBox from '../KamChat/KamChatBox';

const Layout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [productionMenuAnchor, setProductionMenuAnchor] = useState(null);
  const [kamChatOpen, setKamChatOpen] = useState(false);

  // Extract brandId from pathname (e.g., /brands/:brandId/... or /design/:brandId/...)
  const brandId = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Check for patterns like /brands/:id, /design/:id, /campaigns/:brandId/...
    const brandIndex = pathParts.findIndex(part =>
      part === 'brands' || part === 'design' || part === 'brand'
    );
    if (brandIndex !== -1 && pathParts[brandIndex + 1]) {
      return pathParts[brandIndex + 1];
    }
    return null;
  }, [location.pathname]);

  const handleAccountMenuOpen = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleAccountMenuClose();
  };

  const navigationItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      roles: ['all'],
    },
    {
      text: 'Brands',
      icon: <StoreIcon />,
      path: '/brands',
      roles: ['super_admin', 'admin'],
    },
    {
      text: 'Storefront Export',
      icon: <StorefrontIcon />,
      path: '/storefront-export',
      roles: ['super_admin', 'admin'],
    },
    {
      text: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics',
      roles: ['all'],
    },
    {
      text: 'Measurements',
      icon: <MeasurementIcon />,
      path: '/customers/measurements',
      roles: ['super_admin', 'admin'],
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      roles: ['all'],
    },
  ];

  const productionItems = [
    {
      text: 'Partners',
      icon: <PartnersIcon />,
      path: '/partners',
      roles: ['all'],
    },
    {
      text: 'Production Partners',
      icon: <FactoryIcon />,
      path: '/production-partners',
      roles: ['all'],
    },
    {
      text: 'Wholesale Suppliers',
      icon: <SupplierIcon />,
      path: '/wholesale-suppliers',
      roles: ['all'],
    },
    {
      text: 'RFQs',
      icon: <RFQIcon />,
      path: '/rfqs',
      roles: ['all'],
    },
  ];

  const canAccessRoute = (item) => {
    if (item.roles.includes('all')) return true;
    return item.roles.includes(user?.systemRole);
  };

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mr: 4,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.875rem',
              }}
            >
              SS
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Studio Sivad
            </Typography>
          </Box>

          {/* Horizontal Navigation */}
          <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1 }}>
            {navigationItems
              .filter(canAccessRoute)
              .map((item) => (
                <Button
                  key={item.text}
                  onClick={() => navigate(item.path)}
                  startIcon={item.icon}
                  sx={{
                    color: isActiveRoute(item.path) ? 'primary.main' : 'text.primary',
                    bgcolor: isActiveRoute(item.path) ? 'primary.lighter' : 'transparent',
                    textTransform: 'none',
                    fontWeight: isActiveRoute(item.path) ? 600 : 400,
                    px: 2,
                    '&:hover': {
                      bgcolor: isActiveRoute(item.path) ? 'primary.light' : 'action.hover',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}

            {/* Production Menu */}
            <Button
              onClick={(e) => setProductionMenuAnchor(e.currentTarget)}
              startIcon={<FactoryIcon />}
              endIcon={<ExpandMore />}
              sx={{
                color: productionItems.some(item => isActiveRoute(item.path)) ? 'primary.main' : 'text.primary',
                bgcolor: productionItems.some(item => isActiveRoute(item.path)) ? 'primary.lighter' : 'transparent',
                textTransform: 'none',
                fontWeight: productionItems.some(item => isActiveRoute(item.path)) ? 600 : 400,
                px: 2,
                '&:hover': {
                  bgcolor: productionItems.some(item => isActiveRoute(item.path)) ? 'primary.light' : 'action.hover',
                },
              }}
            >
              Production
            </Button>
          </Box>

          {/* Production Dropdown Menu */}
          <Menu
            anchorEl={productionMenuAnchor}
            open={Boolean(productionMenuAnchor)}
            onClose={() => setProductionMenuAnchor(null)}
            PaperProps={{
              elevation: 3,
              sx: { mt: 1.5, minWidth: 200 },
            }}
          >
            {productionItems
              .filter(canAccessRoute)
              .map((item) => (
                <MenuItem
                  key={item.text}
                  onClick={() => {
                    navigate(item.path);
                    setProductionMenuAnchor(null);
                  }}
                  selected={isActiveRoute(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>{item.text}</ListItemText>
                </MenuItem>
              ))}
          </Menu>

          <Box sx={{ flexGrow: 1 }} />

          {/* Notification button */}
          <Tooltip title="Notifications">
            <IconButton sx={{ mr: 1 }}>
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Kam Chat Assistant */}
          <KamChatBox
            context={{
              page: location.pathname,
              brandId: brandId,
              lookName: null,
              cycleLabel: null,
              cycleId: null
            }}
            sessionId={null}
            userId={'507f1f77bcf86cd799439011'}
            onThreadUpdate={(threadId) => {
              console.log('Chat thread updated:', threadId);
            }}
            onOpenChange={setKamChatOpen}
          />

          {/* Account menu */}
          <Tooltip title="Account">
            <IconButton onClick={handleAccountMenuOpen}>
              <Avatar
                sx={{ width: 32, height: 32 }}
                src={user?.profile?.avatar}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Account menu */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        onClick={handleAccountMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1.5, minWidth: 200 },
        }}
      >
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          Profile Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>


      {/* Content Area - Below AppBar */}
      <Box sx={{ display: 'flex', flexGrow: 1, mt: 8 }}>
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: kamChatOpen ? 'calc(100% - 380px)' : '100%',
            height: 'calc(100vh - 64px)',
            overflow: 'auto',
            backgroundColor: 'background.default',
            p: '25px',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Outlet />
        </Box>

        {/* Chat Panel Space - Reserved when chat is open */}
        {kamChatOpen && (
          <Box
            sx={{
              width: 380,
              flexShrink: 0,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default Layout;
